/**
 * Contracts for module "customers". Owner: crm. Mounted at /v1/customers (CRM_DB).
 * Customer 360 read model. Other modules publish facts (plan, entitlements, orders, deliverables, reports,
 * WhatsApp status) via POST /v1/customers/facts (system actor) or the `customers.fact.upsert` job.
 */
import { z } from 'zod';

export const CUSTOMER_FACT_EVENT = 'customers.fact.upsert';

export const PAYMENT_KINDS = ['paid', 'complimentary', 'pending', 'refunded', 'failed'] as const;
export type PaymentKind = (typeof PAYMENT_KINDS)[number];

/** Label shown for an order; a complimentary order is never labelled Paid (QM-C02-001). */
export const ORDER_LABEL_KEYS: Record<PaymentKind, string> = {
  paid: 'customers.orders.paid',
  complimentary: 'customers.orders.complimentary',
  pending: 'customers.orders.pending',
  refunded: 'customers.orders.refunded',
  failed: 'customers.orders.failed',
};

export const CustomerInputSchema = z.object({
  name: z.string().trim().min(1).max(200),
  email: z.string().email().max(254).nullable().optional(),
  externalTenantRef: z.string().max(64).nullable().optional(),
  companyId: z.string().max(64).nullable().optional(),
  ownerId: z.string().max(64).nullable().optional(),
});

/** Row of GET /v1/customers. Everything after `email` is optional and additive. No health or owner label: the CRM store has no source for them. */
export const CustomerListRowSchema = z.object({
  id: z.string(), name: z.string(), email: z.string().nullable(), planCode: z.string().nullable().optional(), planStatus: z.string().nullable().optional(),
  whatsappStatus: z.string().nullable().optional(), ownerId: z.string().nullable().optional(), createdAt: z.string().optional(),
  /** plan lifecycle (plan_status), 'onboarding' while no plan fact has been published */
  status: z.string().optional(),
  /** WhatsApp connection state (connected | pending | disconnected | not_started | error) */
  whatsapp: z.string().optional(),
  /** keys of the customer's active entitlements */
  services: z.array(z.string()).optional(),
});
export type CustomerListRow = z.infer<typeof CustomerListRowSchema>;

export const CustomerFactSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('plan'), customerId: z.string(), planCode: z.string().max(60).nullable(), planStatus: z.string().max(40).nullable() }),
  z.object({ kind: z.literal('entitlement'), customerId: z.string(), key: z.string().max(80), status: z.string().max(40), quantity: z.number().int().nullable().optional() }),
  z.object({
    kind: z.literal('order'), customerId: z.string(), orderRef: z.string().max(100), product: z.string().max(60),
    paymentKind: z.enum(PAYMENT_KINDS), amountMinor: z.number().int().min(0).default(0), currency: z.string().regex(/^[A-Z]{3}$/).default('AED'),
    status: z.string().max(40), orderedAt: z.string().max(40),
  }),
  z.object({ kind: z.literal('deliverable'), customerId: z.string(), ref: z.string().max(100), title: z.string().max(200), status: z.string().max(40), progressPct: z.number().int().min(0).max(100), dueAt: z.string().max(40).nullable().optional() }),
  z.object({ kind: z.literal('report'), customerId: z.string(), period: z.string().regex(/^\d{4}-\d{2}$/), status: z.string().max(40), reportRef: z.string().max(100).nullable().optional() }),
  z.object({ kind: z.literal('whatsapp'), customerId: z.string(), status: z.enum(['connected', 'pending', 'disconnected', 'not_started', 'error']) }),
]);
export type CustomerFact = z.infer<typeof CustomerFactSchema>;
export const CustomerFactsInputSchema = z.object({ facts: z.array(CustomerFactSchema).min(1).max(500) });
